import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";

export type NotificationType =
  | "task_assigned"
  | "task_approved"
  | "task_rejected"
  | "task_pending_approval"
  | "task_comment_added"
  | "project_update"
  | "voucher_pending"
  | "timebank_requested"
  | "timebank_approved"
  | "timebank_rejected"
  | "timebank_cancelled"
  | "feedback_resolved"
  // VIB-1296: daglig cron-varsel ved manglende timeføring siste 2 arbeidsdager.
  | "missing_time_entries"
  /** VIB-1304: 7 dager før offers.valid_until, satt av notify_expiring_offers() cron. */
  | "offer_expiring"
  /**
   * VIB-1653: sykdom/ferie registrert, endret eller slettet av en ansatt.
   * Ren oppfølging for leder — fravær har ingen godkjenningsflyt. Én type
   * dekker alle tre hendelsene, så en leder ikke kan skru av bare «slettet»
   * og dermed miste sporet av en endring den allerede er varslet om.
   */
  | "absence_registered"
  /**
   * VIB-2092: statister meldt klare for utbetaling av PL. Går til DL
   * (admin/superadmin). Ett varsel per prosjekt, ikke per statist — PL melder
   * typisk 20 om gangen, og 20 varsler om samme prosjekt gjør at bjella slutter
   * å bli lest. DB-triggeren dedupliserer på ulest varsel for prosjektet.
   */
  | "extra_ready_for_payment"
  /** VIB-2925: nevnt i en kommentar på en frilanser. Bare til brukere med menyen «Frilansere». */
  | "freelancer_mention"
  /** CRM-2.6: svar på en e-post selgeren sendte fra kundekortet (entity_id = kunde). */
  | "crm_reply_received"
  /** CRM-2.6 (fylles i fase 4): møte booket på deg. */
  | "crm_meeting_booked"
  /** CRM-2.6 (fylles i fase 4): møtereferat klart til godkjenning. */
  | "crm_summary_ready"
  /** CRM-2.6: en annen tok et signal på en kunde du eier. */
  | "crm_signal_claimed"
  /** CRM-2.6: sammendrag av dagens oppfølginger, cron på hverdager. */
  | "crm_follow_ups_today"
  /** VIB-2933: skade meldt på utstyr, til alle utstyrsansvarlige (entity_id = utstyret). */
  | "equipment_damage"
  /** VIB-2933: booket utstyr er på service eller dobbeltbooket, til den ansvarlige for bookingen. */
  | "equipment_booking_alert"
  /** VIB-3025: nevnt med @ i en kanal i Chat-rommet (entity_id = chat_rooms.id). */
  | "chat_mention";

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  project_id: string | null;
  triggered_by: string | null;
  read_at: string | null;
  created_at: string;
  // Joined
  trigger?: { full_name: string } | null;
  project?: { project_code: string | null; name: string | null } | null;
}

const PAGE_SIZE = 30;

/**
 * Hent varsler for innlogget bruker. Realtime-subscription sørger for at
 * cachen invalideres når nye rader dukker opp.
 */
export function useNotifications() {
  const { activeUser } = useImpersonation();
  const queryClient = useQueryClient();
  const userId = activeUser?.id ?? null;

  const query = useQuery({
    queryKey: ["notifications", userId],
    queryFn: async (): Promise<Notification[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("notifications")
        .select(
          "*, trigger:triggered_by(full_name), project:project_id(project_code, name)",
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
    enabled: !!userId,
  });

  // Realtime: oppdater cache når ny rad inserts for denne brukeren
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void invalidateFor(queryClient, "notifications", { userId });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return query;
}

/** Antall uleste varsler (for bjelle-badge). */
export function useUnreadCount() {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;

  return useQuery({
    queryKey: ["unread-count", userId],
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .is("read_at", null);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!userId,
    refetchInterval: 60_000, // backup hvis realtime skulle droppe
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(queryClient, "notifications", { userId });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;

  return useMutation({
    mutationFn: async () => {
      if (!userId) return;
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("user_id", userId)
        .is("read_at", null);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(queryClient, "notifications", { userId });
    },
  });
}
